import type { IVersion } from "@/types";
import type {
  EntityType,
  FlatContentLayersType,
  InitPayloadType,
  StaticEntityType,
  StorySettingsType,
} from "@config";
import { trackEntityAction } from "@editor/utils/analytics";

import { createSlice, nanoid, type PayloadAction } from "@reduxjs/toolkit";
import { setVariableValue } from "@/shared/utils/resolve-tokens";
import { broadcastEvent } from "./collaboration";
import type {
  CollabHistoryStatus,
  CreateEntityType,
  HistoryActionPayload,
  StoryStateType,
  TDocAccessFields,
} from "./types";

import type { ITableSettings } from "@library-elements/table";

import {
  cleanupAnchors,
  copyEntityToClipboard,
  copyNestedToClipboard,
  createEntityHandler,
  deleteChildEntities,
  getCurrentRelativeEntitiesSettings,
  handleChangeEntityLayout,
  handleContentUpdate,
  handleDesignSystem,
  handleDndParent,
  redoHistoryChange,
  removeEntityFromParent,
  undoHistoryChange,
  updateActiveState,
  updateBroadcastActiveState,
  updateEntityChildrenSettings,
  updateTabsDefault,
} from "./utils";
import { copyTableCellsToClipboard } from "./utils/copy-table-cells-to-clipboard";
import { pruneOrphanedCellChildren } from "./utils/prune-orphaned-cell-children";

const initialState: StoryStateType = {
  story: null,
  version: null,
  docAccess: null,
  activeEntityId: null,
  flatContentLayers: {},
  activeThumbnailId: null,
  thumbnailObserverStatus: "enabled",
  activeSettingId: null,
  classifications: null,
  changeId: null,
  overwriteSettingName: null,
  areThumbnailsOpen: true,
  isThumbnailsGridView: false,
  isAutoSaveEnabled: true,
  isDragging: false,
  isFullscreen: false,
  isSaving: false,
  peerGeneratingPreview: false,
  isPublishing: false,
  publishTriggeredBy: null,
  actionOnHold: null,
  activeThreadId: null,
  history: {
    past: [],
    future: [],
    current: { action: "init", changeId: "" },
    isInHistoryUpdate: false,
    isInRedoUndo: false,
    activeAiActionId: null,
    collab: null,
  },
  activeTableCellsIds: [],
  tableFormulaEdit: null,
};

export const storySlice = createSlice({
  name: "story",
  initialState,
  reducers: {
    setStory: (
      state: StoryStateType,
      action: PayloadAction<{ story: InitPayloadType; force: boolean }>,
    ) => {
      const { force = false, story } = action.payload;
      if (state.story !== null && !force) return;
      const { flatContentLayers, slides, ...rest } = story;
      // The caller's role (Send flow) lives in docAccess, never in the shared document.
      delete (rest as TDocAccessFields).myRole;
      delete (rest as TDocAccessFields).isGuest;
      state.story = rest;
      if (window.innerWidth < 1440) state.areThumbnailsOpen = false;
      if (!rest.settings.storyBrainId) {
        state.story.settings.storyBrainId = nanoid(20);
      }
      if (flatContentLayers) {
        storySlice.caseReducers.setFlatContentLayers(state, {
          type: "story/setFlatContentLayers",
          payload: flatContentLayers,
        });
      }
    },
    setVersion: (
      state: StoryStateType,
      action: PayloadAction<IVersion | null>,
    ) => {
      state.version = action.payload;
    },
    setDocAccess: (
      state: StoryStateType,
      action: PayloadAction<TDocAccessFields | null>,
    ) => {
      state.docAccess = action.payload;
    },
    /**
     * Drop the loaded document. Dispatched when the editor route unmounts:
     * the route is keyed by storyId+versionId, so template <-> version
     * navigation remounts the editor, but this slice is global and used to
     * keep the previous document, its version and its last change id until
     * the loader replaced them. Every save that ran in that window saved the
     * wrong document (template 6ab224c4…, 2026-09-24; "Version replaces
     * story", 12660391813). After this the next route mounts over an empty
     * store, exactly like a hard reload. Per-session UI preferences survive.
     */
    resetStory: (state: StoryStateType): StoryStateType => ({
      ...initialState,
      areThumbnailsOpen: state.areThumbnailsOpen,
      isThumbnailsGridView: state.isThumbnailsGridView,
      isAutoSaveEnabled: state.isAutoSaveEnabled,
      // Mirrors a body class toggled outside the store; dropping it here would
      // leave the layout fullscreen with the flag false.
      isFullscreen: state.isFullscreen,
      classifications: state.classifications,
    }),
    setVersionDataValue: (
      state: StoryStateType,
      action: PayloadAction<{
        key: string;
        value: unknown;
        broadcast?: boolean;
      }>,
    ) => {
      if (!state.version?.data) return;
      const { key, value, broadcast } = action.payload;
      setVariableValue(
        state.version.data as Record<string, unknown>,
        key,
        value,
      );
      if (broadcast) return;
      triggerAutosave(state);
      broadcastEvent({
        state,
        event: "set-version-data",
        payload: { key, value },
      });
    },
    setFlatContentLayers: (
      state: StoryStateType,
      action: PayloadAction<FlatContentLayersType>,
    ) => {
      const flatContentLayers = action.payload;
      state.flatContentLayers = flatContentLayers;
      const firstSlide = flatContentLayers["root"].childIds[0];
      state.activeEntityId = firstSlide;
      state.activeThumbnailId = firstSlide;
    },
    restoreStoryFromRevision: (
      state: StoryStateType,
      action: PayloadAction<{
        story: Record<string, unknown>;
        flatContentLayers: FlatContentLayersType;
        broadcast?: boolean;
      }>,
    ) => {
      const { story, flatContentLayers, broadcast } = action.payload;
      state.story = Object.assign({}, state.story, story);
      state.flatContentLayers = flatContentLayers;
      if (
        state.tableFormulaEdit &&
        !flatContentLayers[state.tableFormulaEdit.entityId]
      ) {
        state.tableFormulaEdit = null;
      }
      const firstSlide = flatContentLayers["root"].childIds[0];
      state.activeEntityId = firstSlide;
      state.activeThumbnailId = firstSlide;
      state.history.past = [];
      state.history.future = [];
      state.history.current = { action: "init", changeId: "" };
      if (broadcast) return;
      broadcastEvent({
        state,
        event: "restore-from-revision",
        payload: { story, flatContentLayers },
      });
      triggerAutosave(state);
    },
    // Used by YjsStoryProvider to apply remote whole-tree updates (structural
    // changes, story-settings changes, add/delete events, initial sync). Unlike
    // restoreStoryFromRevision (which models the user clicking "Restore from
    // Revision" and intentionally jumps to the first slide / clears history),
    // this preserves selection, thumbnail, and history because the local user
    // is in the middle of editing.
    syncFromYjs: (
      state: StoryStateType,
      action: PayloadAction<{
        story: Record<string, unknown>;
        flatContentLayers: FlatContentLayersType;
      }>,
    ) => {
      const { story, flatContentLayers } = action.payload;
      // When the reconcile changes the SLIDE LIST (peer added/removed/reordered
      // a slide), pause the slide-in-view IntersectionObserver — it is rebuilt
      // on slideIds.length change and immediately re-picks the "most visible"
      // slide against the shifted layout, yanking the local user's active slide
      // (the collab "jump"). Same guard user actions already use
      // (handleCreateEntity / useActiveThumbnail); useObserveSlideInView
      // auto-re-enables it after 1.5s, once the layout + scroll compensation
      // (preserve-scroll-anchor in YjsStoryProvider) have settled.
      const prevSlideIds = state.flatContentLayers?.root?.childIds;
      const nextSlideIds = flatContentLayers?.root?.childIds;
      if (
        prevSlideIds &&
        nextSlideIds &&
        String(prevSlideIds) !== String(nextSlideIds)
      ) {
        state.thumbnailObserverStatus = "disabled";
      }
      state.story = Object.assign({}, state.story, story);
      state.flatContentLayers = flatContentLayers;
      if (
        state.tableFormulaEdit &&
        !flatContentLayers[state.tableFormulaEdit.entityId]
      ) {
        state.tableFormulaEdit = null;
      }
      const stillExists =
        state.activeEntityId && flatContentLayers[state.activeEntityId];
      if (!stillExists) {
        const firstSlide = flatContentLayers["root"]?.childIds?.[0] || null;
        state.activeEntityId = firstSlide;
        state.activeThumbnailId = firstSlide;
      }
    },
    updateStoryByKeys: (
      state: StoryStateType,
      action: PayloadAction<{
        data: Record<string, unknown>;
        autosave?: boolean;
        broadcast?: boolean;
      }>,
    ) => {
      if (!state.story) return;
      const { data, autosave, broadcast } = action.payload;
      const { settings, ...res } = data;

      state.story = { ...state.story, ...res };
      if (settings) Object.assign(state.story.settings, settings);
      if (broadcast) return;
      if (autosave) triggerAutosave(state);
      // Whole-story convergence invariant: broadcast the FULL story, not just
      // the changed keys. Several flows mutate the story without their own
      // broadcast (publish status via cache refetch, design-system side
      // effects) and rely on the next update-story to re-assert every field —
      // a delta here strands those fields in the Y.Doc's past and the next
      // reconcile reverts them locally. Only `history` is stripped: the
      // per-session history map is local-only (deferred history work) and
      // must not enter the shared doc.
      const { history: _history, ...broadcastStory } = state.story;
      broadcastEvent({
        state,
        event: "update-story",
        payload: { data: broadcastStory },
      });
    },
    updateStorySettings: (
      state: StoryStateType,
      action: PayloadAction<{
        payload: Record<string, unknown>;
        autosave?: boolean;
        broadcast?: boolean;
      }>,
    ) => {
      if (!state.story) return;
      const { payload, autosave, broadcast } = action.payload;
      state.story.settings = { ...state.story.settings, ...payload };
      if (broadcast) return;
      if (autosave) triggerAutosave(state);
      broadcastEvent({
        state,
        event: "update-story-settings",
        payload: { payload },
      });
    },
    setActive(
      state: StoryStateType,
      action: PayloadAction<{ id: string; type?: string }>,
    ) {
      const { id, type } = action.payload;
      if (state.activeEntityId === id) return;
      const entity = state.flatContentLayers[id];
      state.overwriteSettingName = null;
      if (!entity || id === state.activeEntityId) return;
      if (window.innerWidth < 1440 && type !== "thumbnail") {
        state.areThumbnailsOpen = false;
      }
      state.activeEntityId = id;
    },

    // Restores selection after an undo/redo. The undo manager stashes the
    // entity AND the slide the user was on when the step was created. We first
    // try to reselect the entity; if it no longer exists (e.g. undo of a
    // create removed it), syncFromYjs has already fallen back to the FIRST
    // slide — which yanks the user to the top of the deck. Instead we return
    // them to the slide they were on (activeThumbnailId drives the visible
    // slide). If even that slide is gone, we leave syncFromYjs's fallback.
    restoreActiveFromHistory(
      state: StoryStateType,
      action: PayloadAction<{
        entityId?: string | null;
        slideId?: string | null;
      }>,
    ) {
      const { entityId, slideId } = action.payload;
      const layers = state.flatContentLayers;
      if (entityId && layers[entityId]) {
        state.activeEntityId = entityId;
        return;
      }
      if (slideId && layers[slideId]) {
        state.activeEntityId = slideId;
        state.activeThumbnailId = slideId;
      }
    },

    // ======== AUTOSAVE TRIGGER REDUCERS ========
    editEntity(
      state: StoryStateType,
      action: PayloadAction<{
        data: Record<string, unknown>;
        id?: string;
        full?: boolean;
        children?: boolean;
        broadcast?: boolean;
        skipHistory?: boolean;
      }>,
    ) {
      const {
        data,
        id: payloadId,
        full,
        children,
        broadcast,
        skipHistory,
      } = action.payload;
      const flatContentLayers = state.flatContentLayers;
      const id = payloadId || state.activeEntityId || "";
      const entity = flatContentLayers[id];
      if (!entity) return;
      const currentSettings: Record<string, unknown> = {};
      const childCurrentSettings: Record<string, Record<string, unknown>> = {};
      // =========== TABS PIN DEFAULT ===========
      const relativeCurrentEntitiesSettings: Record<
        string,
        Record<string, unknown>
      > = data.isTabDefault
        ? getCurrentRelativeEntitiesSettings(
            entity.parentId as string,
            flatContentLayers,
          )
        : {};
      // =========== TABS PIN DEFAULT ===========

      const isMedia = ["Image", "Logo", "Video"].includes(
        entity.settings.name as string,
      );

      if (isMedia) {
        const payloadMetadata =
          (data.metadata as Record<string, unknown>) || {};
        const metadata =
          (entity.settings.metadata as Record<string, unknown>) || {};
        if (data.src) {
          let cropperBase = {};
          if (["Image", "Logo"].includes(entity.settings.name as string)) {
            cropperBase = {
              scale: 1,
              translateX: 50,
              translateY: 50,
            };

            // Clear crop data when src changes from a different source
            // (not from the crop dialog, which sets cropOriginalSrc in a separate dispatch).
            if (!("cropOriginalSrc" in data) && !("cropData" in data)) {
              data.cropOriginalSrc = "";
              data.cropData = undefined;
            }
          }

          data.metadata = {
            ...metadata,
            width: undefined,
            height: undefined,
            imageBlurhash: undefined,
            ...cropperBase,
            ...payloadMetadata,
          };
        } else if (data.metadata) {
          data.metadata = {
            ...metadata,
            ...payloadMetadata,
          };
        }
      }

      const handleEdit = () => {
        if (full) {
          Object.assign(currentSettings, entity.settings);
          entity.settings = data;
        } else {
          Object.keys(data).forEach((key) => {
            currentSettings[key] = entity.settings[key];
            entity.settings[key] = data[key];
          });
        }
        if (children) {
          updateEntityChildrenSettings(
            entity,
            flatContentLayers,
            data,
            childCurrentSettings,
          );
        }

        if (data.isTabDefault) {
          updateTabsDefault(id, flatContentLayers);
        }
      };

      // Orphan-prune invariant: a tableData rewrite (merge, row/col delete,
      // clear) must drop in-cell element children whose cell is gone — in the
      // SAME reducer pass, on every peer (the prune is deterministic, so the
      // broadcast path re-derives the identical result).
      let pruned: ReturnType<typeof pruneOrphanedCellChildren>;
      const runEdit = () => {
        handleEdit();
        if (full || "tableData" in data) {
          pruned = pruneOrphanedCellChildren(state, id);
        }
      };

      if (broadcast) {
        handleContentUpdate(runEdit);
        return;
      } else runEdit();

      // A write that changes nothing — Froala re-saving identical content
      // after a pill caret-guard tweak, the unmount commit of an element the
      // user only clicked into — must not become a history frame: undoing it
      // is invisible, so the first Ctrl+Z "did nothing" while arming redo.
      // A tableData rewrite that dropped orphaned in-cell children changed the
      // deck even when the cells themselves read the same: keep its frame so
      // undo can bring those children back. Only STRING values are compared —
      // the phantom re-saves are `content` strings, and serializing whole
      // tableData/metadata objects on every commit to prove a change was work
      // on the typing hot path; a non-string edit simply records.
      const unchanged =
        !full &&
        !children &&
        !data.isTabDefault &&
        !pruned?.rootIds.length &&
        Object.keys(data).every(
          (key) =>
            typeof data[key] === "string" && data[key] === currentSettings[key],
        );
      if (!skipHistory && !unchanged) {
        handleHistory(state, {
          action: "edit",
          id,
          currentSettings,
          childCurrentSettings,
          relativeCurrentEntitiesSettings,
          payload: { data, full, children },
          ...(pruned && {
            prunedChildren: {
              entities: pruned.entities,
              indexes: pruned.indexes,
            },
          }),
          changeId: nanoid(),
        });
      } else triggerAutosave(state);
      trackEntityAction({
        entity,
        action: "edit",
        flatContentLayers,
        id: state.story?._id,
      });
      broadcastEvent({
        state,
        event: "edit",
        payload: {
          data,
          id,
          full,
          children,
          // Pruned subtree ids so the Yjs mapper emits matching delete ops —
          // without them the Y.Doc keeps the orphans and reconcile resurrects.
          ...(pruned && { prunedChildIds: Object.keys(pruned.entities) }),
        },
      });
    },
    editManyEntities(
      state: StoryStateType,
      action: PayloadAction<{
        data: Record<string, Record<string, unknown>>;
        broadcast?: boolean;
        // See EditManyPayloadType.fsPatch: lets a caller (e.g.
        // repeatable.thunk.ts) fold a settings.fs before/after snapshot into
        // THIS SAME undo/redo frame. Not applied here — the caller is
        // expected to also dispatch the actual settings write (typically via
        // updateStoryByKeys) right after; this only records it for
        // undo/redo replay.
        fsPatch?: {
          current: StorySettingsType["fs"];
          next: StorySettingsType["fs"];
        };
      }>,
    ) {
      const { data, broadcast, fsPatch } = action.payload;

      const current: Record<string, unknown> = {};

      Object.entries(data).forEach(([id, payload]) => {
        const entity = state.flatContentLayers[id];
        if (!entity) return;
        const currentSettings: Record<string, unknown> = {};
        Object.keys(payload).forEach((key) => {
          currentSettings[key] = entity.settings[key];
          entity.settings[key] = payload[key];
        });
        current[id] = currentSettings;
      });

      if (broadcast) return;

      handleHistory(state, {
        action: "edit-many",
        current,
        payload: data,
        changeId: nanoid(),
        ...(fsPatch && { fsPatch }),
      });
      broadcastEvent({ state, event: "edit-many", payload: { data } });
    },
    deleteEntity(
      state: StoryStateType,
      action: PayloadAction<{
        id?: string;
        broadcast?: boolean;
      }>,
    ) {
      const flatContentLayers = state.flatContentLayers;
      const { id: payloadId, broadcast } = action.payload;
      let id = payloadId || state.activeEntityId || "";
      let entity = flatContentLayers[id];
      if (!entity) return;
      let parent = flatContentLayers[entity.parentId || ""];
      if (entity.type === "slide" && parent && parent.childIds.length === 1)
        return;
      if (parent && parent.type === "combo" && parent.childIds.length === 1) {
        state.activeEntityId = parent.id;
        id = parent.id;
        entity = parent;
        parent = flatContentLayers[entity.parentId || ""];
      }

      const index = parent.childIds.indexOf(id);
      const deletedEntities: Record<string, EntityType> = {};

      const clearFormulaEditIfDeleted = () => {
        const fe = state.tableFormulaEdit;
        if (fe && (fe.entityId === entity.id || deletedEntities[fe.entityId])) {
          state.tableFormulaEdit = null;
        }
      };

      if (broadcast) {
        handleContentUpdate(() => {
          updateBroadcastActiveState(state, entity, flatContentLayers, id);
          removeEntityFromParent(entity, flatContentLayers);
          deleteChildEntities(entity, flatContentLayers, deletedEntities);
        });
        cleanupAnchors(state, deletedEntities);
        clearFormulaEditIfDeleted();
        return;
      } else {
        updateActiveState(state, entity, flatContentLayers);
        removeEntityFromParent(entity, flatContentLayers);
        deleteChildEntities(entity, flatContentLayers, deletedEntities);
        cleanupAnchors(state, deletedEntities);
        clearFormulaEditIfDeleted();
      }
      const updatedActiveId = state.activeEntityId;
      handleHistory(state, {
        action: "delete",
        rootId: entity.id,
        entities: deletedEntities,
        index,
        updatedActiveId,
        changeId: nanoid(),
      });
      trackEntityAction({
        entity,
        action: "delete",
        flatContentLayers,
        id: state.story?._id,
      });
      broadcastEvent({ state, event: "delete", payload: { id } });
    },
    createEntity(
      state: StoryStateType,
      action: PayloadAction<CreateEntityType & { broadcast?: boolean }>,
    ) {
      const { broadcast } = action.payload;
      const initialActiveId = state.activeEntityId;
      const initialActiveThumbnailId = state.activeThumbnailId ?? undefined;
      let historyPayload = null;
      if (broadcast) {
        handleContentUpdate(() => {
          historyPayload = createEntityHandler(action.payload, state);
        });
        return;
      } else {
        historyPayload = createEntityHandler(action.payload, state);
      }
      if (!historyPayload) return;
      handleHistory(state, {
        action: "create",
        changeId: nanoid(),
        initialActiveId,
        initialActiveThumbnailId,
        ...historyPayload,
      });
    },
    changeEntityLayout(
      state: StoryStateType,
      action: PayloadAction<{
        parentId: string;
        original: StaticEntityType;
        changed: StaticEntityType;
        broadcast?: boolean;
        type: "slide" | "combo";
      }>,
    ) {
      const { parentId, original, changed, broadcast, type } = action.payload;
      if (broadcast) {
        handleContentUpdate(() => {
          handleChangeEntityLayout(parentId, original, changed, state, type);
        });
        return;
      }
      handleChangeEntityLayout(parentId, original, changed, state, type);
      handleHistory(state, {
        action: "change-entity-layout",
        changeId: nanoid(),
        payload: { parentId, original, changed, type },
      });
      broadcastEvent({
        state,
        event: "change-entity-layout",
        payload: { parentId, original, changed, type },
      });
    },
    reorderEntities(
      state: StoryStateType,
      action: PayloadAction<
        (
          | {
              activeId: string;
              overId: string;
              parentId: string;
              activeParentId?: string;
              type: "reorder";
              isFirstEntity?: boolean;
              // Which side of `overId` the element lands on, taken from the
              // pointer half the drop indicator was drawn against. Omitted by
              // callers that have no pointer (AI handlers, thumbnails, combo
              // drags), which keep the legacy arrayMove placement.
              insertBefore?: boolean;
            }
          | { type: "swap"; direction: "backward" | "forward"; id?: string }
        ) & { broadcast?: boolean }
      >,
    ) {
      const broadcastPayload = {};
      const historyPayload = {
        id: "",
        currentParentId: "",
        updatedParentId: "",
        currentIndex: -1,
        updatedIndex: -1,
      };

      const handleReorder = () => {
        if (action.payload.type === "swap") {
          const { id, direction } = action.payload;
          const active = id || state.activeEntityId;
          if (!active) return;
          const parent = state.flatContentLayers[active].parentId;
          if (!parent) return;
          const parentEntity = state.flatContentLayers[parent];
          const index = parentEntity.childIds.indexOf(active);
          const newIndex = direction === "forward" ? index + 1 : index - 1;
          if (newIndex < 0 || newIndex >= parentEntity.childIds.length) return;
          const reorderEntity = parentEntity.childIds[newIndex];
          parentEntity.childIds[index] = reorderEntity;
          parentEntity.childIds[newIndex] = active;

          historyPayload.id = active;
          historyPayload.currentIndex = index;
          historyPayload.updatedIndex = newIndex;
          historyPayload.currentParentId = parent;
          historyPayload.updatedParentId = parent;
          Object.assign(broadcastPayload, {
            id: active,
            direction,
            type: "swap",
          });
        }
        if (action.payload.type === "reorder") {
          const {
            activeParentId,
            parentId,
            activeId,
            overId,
            isFirstEntity,
            insertBefore,
          } = action.payload;
          const parent = state.flatContentLayers[parentId];
          if (!parent) return;
          if (activeParentId && activeParentId !== parentId) {
            const activeParent = state.flatContentLayers[activeParentId];
            historyPayload.currentIndex =
              activeParent.childIds.indexOf(activeId);
            handleDndParent(state, {
              newParentId: parentId as string,
              currentParentId: activeParentId,
              id: activeId,
              overId,
            });
          }
          const activeIndex = parent.childIds.indexOf(activeId);
          // Anchor index as the legacy callers saw it: read BEFORE the dragged
          // id is pulled out, then reused as the insert slot afterwards. That
          // combination is arrayMove — it lands after the anchor when dragging
          // down and before it when dragging up, whatever the pointer did.
          const overIndexBefore = parent.childIds.indexOf(overId);
          parent.childIds.splice(activeIndex, 1);
          // Re-resolved against the list the insert actually happens on, so
          // `insertAt` means literally "the slot before / after overId".
          const overIndex = parent.childIds.indexOf(overId);
          let insertAt: number;
          if (isFirstEntity) {
            insertAt = 0;
          } else if (insertBefore == null) {
            insertAt =
              overIndexBefore < 0 ? parent.childIds.length : overIndexBefore;
          } else if (overIndex < 0) {
            // Anchor isn't a child of the target (empty group, stale overId) —
            // append rather than let splice(-1) wedge it before the last item.
            insertAt = parent.childIds.length;
          } else {
            insertAt = insertBefore ? overIndex : overIndex + 1;
          }
          parent.childIds.splice(insertAt, 0, activeId);

          historyPayload.id = activeId;
          historyPayload.updatedIndex = insertAt;
          historyPayload.currentParentId = activeParentId || parentId;
          historyPayload.updatedParentId = parentId;
          if (historyPayload.currentIndex === -1)
            historyPayload.currentIndex = activeIndex;
          Object.assign(broadcastPayload, action.payload);
        }
      };

      if (action.payload.broadcast) {
        handleContentUpdate(handleReorder);
        return;
      } else {
        handleReorder();
      }

      handleHistory(state, {
        action: "reorder",
        payload: historyPayload,
        changeId: nanoid(),
      });

      broadcastEvent({
        state,
        event: "reorder-entities",
        payload: broadcastPayload,
      });
    },
    updateDesignSystem(
      state: StoryStateType,
      action: PayloadAction<{
        payload: {
          design: Record<string, unknown>;
          entities?: {
            [name: string]:
              | Record<string, unknown>
              | ((data: Record<string, unknown>) => Record<string, unknown>);
          };
        };
        broadcast?: boolean;
      }>,
    ) {
      const {
        payload: { design, entities },
        broadcast,
      } = action.payload;
      const parsed = handleDesignSystem({
        state,
        design,
        entities,
      });

      if (!parsed || broadcast) return;
      const { appliedEntities, ...historyCurrent } = parsed;
      handleHistory(state, {
        action: "design-system",
        current: historyCurrent,
        payload: { design, entities },
        changeId: nanoid(),
      });

      broadcastEvent({
        state,
        event: "design-system",
        payload: {
          design,
          entities,
          // Computed per-entity results for the Yjs bridge: peers can't
          // re-run the `entities` recipe (it can hold functions and reads
          // this client's state), so they receive the outcomes instead.
          appliedEntities,
          // The Y.Doc stores designSystemV2 wholesale — send the merged
          // object so a partial diff doesn't drop peers' other keys.
          mergedDesign: state.story?.settings?.designSystemV2,
        },
      });
      triggerAutosave(state);
    },
    // ======== AUTOSAVE TRIGGER REDUCERS ========
    getEntityToClipboard(
      state: StoryStateType,
      action: PayloadAction<{ id?: string; cut?: boolean }>,
    ) {
      const { id: payloadId, cut = false } = action.payload;
      const id = payloadId || state.activeEntityId || "";
      if (cut)
        updateActiveState(
          state,
          state.flatContentLayers[id],
          state.flatContentLayers,
        );
      copyEntityToClipboard(id, state.flatContentLayers, cut);
    },
    getNestedToClipboard(state: StoryStateType, action: PayloadAction<string>) {
      const id = action.payload;
      copyNestedToClipboard({ id, flatContentLayers: state.flatContentLayers });
    },
    // ======== THUMBNAIL ACTIONS ========
    setActiveThumbnailId(
      state: StoryStateType,
      action: PayloadAction<{ id: string }>,
    ) {
      // Guarantee activeThumbnailId always resolves to a slide that's in
      // root.childIds. The single-slide canvas (Content.tsx
      // SingleSlideContent) renders activeThumbnailId verbatim, so if a
      // stale id ever leaks in (a thumbnail click for a slide that was
      // since reordered/replaced via collab broadcast, an undo/redo
      // restoring an old active id, etc.) the canvas paints a phantom
      // "Slide 0" because slideIds.indexOf(id) returns -1. Validate here
      // and snap to the closest real slide instead.
      const requested = action.payload.id;
      const slideIds = state.flatContentLayers.root?.childIds ?? [];
      if (!slideIds.length) {
        // No slides yet (story load race) — refuse to set a value
        // we can't validate. Content.tsx renders nothing for null,
        // so the user briefly sees an empty canvas instead of a
        // phantom "Slide 0".
        state.activeThumbnailId = null;
        return;
      }
      state.activeThumbnailId = slideIds.includes(requested)
        ? requested
        : slideIds[0];
    },
    setThumbnailObserverStatus(
      state: StoryStateType,
      action: PayloadAction<"disabled" | "enabled">,
    ) {
      state.thumbnailObserverStatus = action.payload;
    },
    setThumbnailsOpenStatus(
      state: StoryStateType,
      action: PayloadAction<boolean>,
    ) {
      state.areThumbnailsOpen = action.payload;
    },
    setThumbnailsIsGridView(
      state: StoryStateType,
      action: PayloadAction<boolean>,
    ) {
      state.isThumbnailsGridView = action.payload;
      if (action.payload) document.body.classList.add("--thumbnails-grid-view");
      else document.body.classList.remove("--thumbnails-grid-view");
    },
    // ======== THUMBNAIL ACTIONS ========
    setActiveSettingId(
      state: StoryStateType,
      action: PayloadAction<{ id: string | null }>,
    ) {
      state.activeSettingId = action.payload.id;
    },
    setClassifications: (state: StoryStateType, action: PayloadAction<any>) => {
      state.classifications = action.payload;
    },
    handleCustomColor: (
      state: StoryStateType,
      action: PayloadAction<
        (
          | { action: "remove"; index: number }
          | { action: "add"; color: string }
          | { action: "update"; index: number; color: string }
        ) & { broadcast?: boolean }
      >,
    ) => {
      const { broadcast } = action.payload;
      if (!state.story) return;
      let customColors = state.story.settings.customColors || [];
      if (action.payload.action === "remove")
        customColors.splice(action.payload.index, 1);
      else if (action.payload.action === "update") {
        customColors[action.payload.index] = action.payload.color;
      } else {
        customColors.push(action.payload.color);
        customColors = customColors.filter((v, i, a) => a.indexOf(v) === i);
      }
      state.story.settings.customColors = customColors;
      if (broadcast) return;
      triggerAutosave(state);
      // Pass the freshly-mutated array in the payload — the Yjs bridge runs
      // synchronously here, before React re-renders, so any provider-side ref
      // to `story.settings` is still stale at this point.
      broadcastEvent({
        state,
        event: "handle-colors",
        payload: { ...action.payload, customColors },
      });
    },
    handleAnchor(
      state: StoryStateType,
      action: PayloadAction<{
        value: string;
        id?: string;
        broadcast?: boolean;
      }>,
    ) {
      const { value, id: payloadId, broadcast } = action.payload;
      const id = payloadId || state.activeEntityId || "";
      if (!state.story || !id) return;
      const anchors = state.story.settings.anchors;
      if (!value) delete anchors?.[id];
      else {
        if (anchors) anchors[id] = value;
        else
          state.story.settings.anchors = {
            [id]: value,
          };
      }
      if (broadcast) return;
      // Pass the freshly-mutated map in the payload — the Yjs bridge runs
      // synchronously here, before React re-renders, so any provider-side ref
      // to `story.settings` is still stale at this point.
      broadcastEvent({
        state,
        event: "handle-anchors",
        payload: { value, id, anchors: state.story.settings.anchors || {} },
      });
    },
    setOverwriteSettingName: (
      state: StoryStateType,
      action: PayloadAction<string | null>,
    ) => {
      state.overwriteSettingName = action.payload;
    },
    handleAutosave: (state: StoryStateType, action: PayloadAction<boolean>) => {
      state.isAutoSaveEnabled = action.payload;
    },
    setIsDragging: (state: StoryStateType, action: PayloadAction<boolean>) => {
      state.isDragging = action.payload;
    },
    toggleFullScreenLayout: (state: StoryStateType) => {
      state.isFullscreen = !state.isFullscreen;
      if (state.isFullscreen)
        document.body.classList.add("--fullscreen-layout");
      else document.body.classList.remove("--fullscreen-layout");
    },
    setIsSaving: (state: StoryStateType, action: PayloadAction<boolean>) => {
      state.isSaving = action.payload;
    },
    setPeerGeneratingPreview: (
      state: StoryStateType,
      action: PayloadAction<boolean>,
    ) => {
      state.peerGeneratingPreview = action.payload;
    },
    setIsPublishing: (
      state: StoryStateType,
      action: PayloadAction<boolean>,
    ) => {
      state.isPublishing = action.payload;
      if (!action.payload) state.publishTriggeredBy = null;
    },
    setPublishTriggeredBy: (
      state: StoryStateType,
      action: PayloadAction<"share" | "export-pdf" | null>,
    ) => {
      state.publishTriggeredBy = action.payload;
    },
    setActionOnHold: (
      state: StoryStateType,
      action: PayloadAction<
        "share" | "export-pdf" | "preview" | "design-system" | null
      >,
    ) => {
      state.actionOnHold = action.payload;
    },
    resetState(state: StoryStateType) {
      Object.assign(state, initialState);
    },

    // ======== HISTORY ACTIONS ========
    updateHistory: (state: StoryStateType, action: PayloadAction<any>) => {
      if (state.history.current) state.history.past.push(state.history.current);
      if (state.history.past.length >= 100) state.history.past.shift();
      const payload = action.payload;
      state.history.current = payload;
      state.history.future = [];
    },
    // Single-user undo/redo. In a collaborative room these are NOT dispatched —
    // useStoryHistory routes to the Yjs UndoManager instead (undo-manager.ts) —
    // so there is no longer any broadcast of the undo/redo effect from here.
    //
    // AN AI BATCH IS ONE STEP. Every frame a turn pushes carries the turn's
    // aiActionId, with the `ai-checkpoint` marker as the batch's OLDEST frame
    // (pushAiCheckpoint). Undo keeps popping while the frame it is undoing
    // carries the id it started on, so the marker is the last frame to go and
    // the deck lands exactly where it was before the turn. "Make the title
    // italic" used to bulk-edit 7 elements as 7 frames and one undo took back
    // ONE of them. A frame with no id pops alone, as before.
    handleUndo: (state: StoryStateType) => {
      if (state.history.past.length === 0) return;
      state.history.isInRedoUndo = true;
      const batchId = state.history.current?.aiActionId;
      let previous: HistoryActionPayload | undefined;
      do {
        const undone = state.history.current;
        undoHistoryChange(state, undone);
        if (undone) state.history.future.unshift(undone);
        previous = state.history.past.pop();
        state.history.current = previous;
        // The marker closes the batch even if an older frame somehow carries
        // the same id.
        if (!batchId || undone?.action === "ai-checkpoint") break;
      } while (
        state.history.past.length > 0 &&
        state.history.current?.aiActionId === batchId
      );
      if (previous) previous.changeId = nanoid();
    },
    // Symmetric: a marker at the head of `future` replays its whole batch. A
    // plain frame there — tagged or not — redoes one step, as before.
    handleRedo: (state: StoryStateType) => {
      if (state.history.future.length === 0) return;
      state.history.isInRedoUndo = true;
      const head = state.history.future[0];
      const batchId =
        head?.action === "ai-checkpoint" ? head.aiActionId : undefined;
      let next: HistoryActionPayload | undefined;
      do {
        next = state.history.future.shift();
        redoHistoryChange(state, next);
        if (state.history.current)
          state.history.past.push(state.history.current);
        state.history.current = next;
      } while (
        batchId !== undefined &&
        state.history.future.length > 0 &&
        state.history.future[0]?.aiActionId === batchId
      );
      if (next) next.changeId = nanoid();
    },
    handleBroadcastHistory: (
      state: StoryStateType,
      action: PayloadAction<{
        actionType: "handle-undo" | "handle-redo";
        payload: Record<string, unknown>;
      }>,
    ) => {
      const { actionType, payload } = action.payload;
      handleContentUpdate(() => {
        if (actionType === "handle-undo")
          undoHistoryChange(state, { ...payload, broadcast: true });
        else redoHistoryChange(state, { ...payload, broadcast: true });
      });
    },
    isIsInRedoUndo: (state: StoryStateType, action: PayloadAction<boolean>) => {
      state.history.isInRedoUndo = action.payload;
    },
    // Flags a local content change so the autosave effect fires, WITHOUT
    // touching content — reuses triggerAutosave, which bumps
    // history.current.changeId (the field getLastChangeId/the autosave watches).
    // Collab undo/redo go through the Yjs UndoManager → observer ("remote")
    // path and never bump changeId, so without this the autosave never fires on
    // undo and the durable contentPointer stays pre-undo (refresh reverts it).
    // Safe now that the autosave is broadcast:true (no Y.Doc write → no undo
    // pollution). Dispatched AFTER undo/redo reconciles, so the post-undo state
    // is what gets saved.
    markContentDirty: (state: StoryStateType) => {
      triggerAutosave(state);
    },
    resetHistory: (state: StoryStateType) => {
      // The collab mirror belongs to the mounted YjsStoryProvider, not to
      // this history; wiping it would gate the AI Undo on the Redux frames.
      state.history = { ...initialState.history, collab: state.history.collab };
    },
    // Pushes a no-op marker frame onto the history. Used by the
    // AI agent to bracket multi-dispatch operations: every entity
    // edit it performs lands as a normal `edit` frame on top of
    // the marker, and a single "Undo this AI change" call walks
    // the history back to (and including) the marker.
    pushAiCheckpoint: (
      state: StoryStateType,
      action: PayloadAction<{
        aiActionId: string;
        label?: string;
      }>,
    ) => {
      const { aiActionId, label } = action.payload;
      // Set the active id BEFORE pushing the marker so handleHistory
      // doesn't try to re-tag the marker (which already carries the
      // id). Every subsequent frame this AI op pushes will inherit
      // it via handleHistory.
      state.history.activeAiActionId = aiActionId;
      handleHistory(state, {
        action: "ai-checkpoint",
        changeId: nanoid(),
        aiActionId,
        label,
      });
    },
    // With an id, clears only if that op's batch is still the active one, so
    // a build ending cannot close a batch another op has opened since.
    clearActiveAiActionId: (
      state: StoryStateType,
      action: PayloadAction<{ aiActionId: string } | undefined>,
    ) => {
      const id = action.payload?.aiActionId;
      if (id && state.history.activeAiActionId !== id) return;
      state.history.activeAiActionId = null;
    },
    // What the collaborative room's Yjs undo stack looks like right now —
    // written by YjsStoryProvider on every stack change, cleared to null when
    // it unmounts. Read through history-selectors.ts, never directly: in a
    // room the frames in this slice are pushed but never popped, so they
    // cannot say what an undo would revert; this can.
    setCollabHistoryStatus: (
      state: StoryStateType,
      action: PayloadAction<CollabHistoryStatus | null>,
    ) => {
      state.history.collab = action.payload;
    },
    // Removes a marker a turn pushed but never followed with a change (its
    // handler touched nothing), so the turn does not offer an Undo over an
    // empty frame. Acts only when the NEWEST frame is that very marker;
    // nothing goes onto `future` — there is nothing to redo.
    dropEmptyAiCheckpoint: (
      state: StoryStateType,
      action: PayloadAction<{ aiActionId: string }>,
    ) => {
      const { aiActionId } = action.payload;
      const current = state.history.current;
      if (
        !current ||
        current.action !== "ai-checkpoint" ||
        current.aiActionId !== aiActionId
      )
        return;
      state.history.current = state.history.past.pop();
      // With the marker gone the tag has nothing to collapse into.
      if (state.history.activeAiActionId === aiActionId)
        state.history.activeAiActionId = null;
    },
    // ======== HISTORY ACTIONS ========
    setActiveThreadId: (
      state: StoryStateType,
      action: PayloadAction<string | null>,
    ) => {
      state.activeThreadId = action.payload;
    },

    // ======== TABLE ELEMENT ========
    setTableActiveCellsIds: (
      state: StoryStateType,
      action: PayloadAction<string[]>,
    ) => {
      state.activeTableCellsIds = action.payload;
    },
    // In-cell formula edit: start opens the input, update rewrites the buffer,
    // stop clears UI state without writing (commit happens via editTableEntity).
    startTableFormulaEdit: (
      state: StoryStateType,
      action: PayloadAction<{
        entityId: string;
        cellId: string;
        buffer: string;
      }>,
    ) => {
      state.tableFormulaEdit = action.payload;
    },
    updateTableFormulaEdit: (
      state: StoryStateType,
      action: PayloadAction<string>,
    ) => {
      if (!state.tableFormulaEdit) return;
      state.tableFormulaEdit.buffer = action.payload;
    },
    stopTableFormulaEdit: (state: StoryStateType) => {
      state.tableFormulaEdit = null;
    },
    getTableCellsToClipboard(
      state: StoryStateType,
      action: PayloadAction<{ layerId?: string; cellsIds: string[] }>,
    ) {
      const { layerId, cellsIds } = action.payload;
      const id = layerId || state.activeEntityId || "";
      copyTableCellsToClipboard(id, cellsIds, state.flatContentLayers);
    },
    selectTableNextCell: (
      state: StoryStateType,
      action: PayloadAction<{ layerId?: string }>,
    ) => {
      const { layerId } = action.payload;
      const table =
        state.flatContentLayers[layerId || state.activeEntityId || ""];
      if (!table) return;
      const settings = table.settings as unknown as ITableSettings;

      const cellIdx = settings.tableData.findIndex(
        (el) => el.id === state.activeTableCellsIds[0],
      );

      if (cellIdx < 0) {
        const cellId = settings.tableData[0]?.id;
        if (cellId) {
          state.activeTableCellsIds = [cellId];
        }
        return;
      }
      const nextCell = settings.tableData[cellIdx + 1];
      if (nextCell) {
        state.activeTableCellsIds = [nextCell.id];
      } else {
        state.activeTableCellsIds = [settings.tableData[0].id];
      }
    },
    selectTablePrevCell: (
      state: StoryStateType,
      action: PayloadAction<{ layerId?: string }>,
    ) => {
      const { layerId } = action.payload;
      const table =
        state.flatContentLayers[layerId || state.activeEntityId || ""];
      if (!table) return;
      const settings = table.settings as unknown as ITableSettings;

      const cellIdx = settings.tableData.findIndex(
        (el) => el.id === state.activeTableCellsIds[0],
      );

      if (cellIdx < 0) {
        const lastCell = settings.tableData[settings.tableData.length - 1]?.id;
        if (lastCell) {
          state.activeTableCellsIds = [lastCell];
        }
        return;
      }
      if (cellIdx > 0) {
        state.activeTableCellsIds = [settings.tableData[cellIdx - 1].id];
      } else {
        state.activeTableCellsIds = [
          settings.tableData[settings.tableData.length - 1].id,
        ];
      }
    },
    // ===============================
  },
});

const handleHistory = (
  state: StoryStateType,
  payload: HistoryActionPayload,
) => {
  // Tag the frame with the currently-active AI op id so the undo
  // loop can collapse the whole batch into one Cmd+Z. Already-set
  // aiActionId on the payload (e.g. the marker frame itself) wins.
  const tagged: HistoryActionPayload =
    state.history.activeAiActionId && payload.aiActionId === undefined
      ? { ...payload, aiActionId: state.history.activeAiActionId }
      : payload;
  storySlice.caseReducers.updateHistory(state, {
    type: "story/updateHistory",
    payload: tagged,
  });
};

const triggerAutosave = (state: StoryStateType) => {
  state.history.current = {
    ...state.history.current,
    changeId: nanoid(),
  } as HistoryActionPayload;
};

export const {
  setStory,
  setVersion,
  setDocAccess,
  resetStory,
  setVersionDataValue,
  setFlatContentLayers,
  setActive,
  restoreActiveFromHistory,
  setActiveThumbnailId,
  editEntity,
  reorderEntities,
  deleteEntity,
  getEntityToClipboard,
  getNestedToClipboard,
  getTableCellsToClipboard,
  setActiveSettingId,
  resetState,
  setClassifications,
  handleCustomColor,
  handleAnchor,
  setOverwriteSettingName,
  setThumbnailObserverStatus,
  updateStoryByKeys,
  updateStorySettings,
  createEntity,
  setThumbnailsOpenStatus,
  setThumbnailsIsGridView,
  handleUndo,
  handleRedo,
  handleAutosave,
  pushAiCheckpoint,
  clearActiveAiActionId,
  dropEmptyAiCheckpoint,
  setCollabHistoryStatus,
  resetHistory,
  isIsInRedoUndo,
  markContentDirty,
  setIsDragging,
  toggleFullScreenLayout,
  setIsSaving,
  setPeerGeneratingPreview,
  setIsPublishing,
  setPublishTriggeredBy,
  setActionOnHold,
  setActiveThreadId,
  handleBroadcastHistory,
  restoreStoryFromRevision,
  syncFromYjs,
  updateDesignSystem,
  editManyEntities,
  changeEntityLayout,
  setTableActiveCellsIds,
  startTableFormulaEdit,
  updateTableFormulaEdit,
  stopTableFormulaEdit,
  selectTableNextCell,
  selectTablePrevCell,
} = storySlice.actions;
